import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Link,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { Link as RouterLink } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

const validationSchema = yup.object({
  email: yup.string().email('Geben Sie eine gültige E-Mail ein').required('E-Mail ist erforderlich'),
});

const ForgotPasswordPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const formik = useFormik({
    initialValues: {
      email: '',
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      setLoading(true);
      setError(null);

      try {
        await axios.post(`${API_BASE_URL}/auth/forgot-password`, {
          email: values.email,
        });
        setSuccess(true);
      } catch (err: any) {
        const detail = err.response?.data?.detail;
        if (Array.isArray(detail)) {
          setError(detail.map((e: any) => e.msg || JSON.stringify(e)).join(', '));
        } else if (typeof detail === 'string') {
          setError(detail);
        } else {
          setError('Anfrage fehlgeschlagen. Bitte versuchen Sie es erneut.');
        }
      } finally {
        setLoading(false);
      }
    },
  });

  return (
    <>
      <Helmet>
        <title>Passwort vergessen - BTrader</title>
      </Helmet>
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'background.default',
          p: 2,
        }}
      >
        <Card sx={{ maxWidth: 400, width: '100%' }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h4" component="h1" gutterBottom textAlign="center">
              Passwort vergessen
            </Typography>
            <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mb: 3 }}>
              Geben Sie Ihre E-Mail-Adresse ein und wir senden Ihnen einen Link zum Zurücksetzen.
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {success ? (
              <Alert severity="success" sx={{ mb: 2 }}>
                Wenn ein Konto mit dieser E-Mail existiert, wurde eine E-Mail zum Zurücksetzen gesendet.
                Bitte prüfen Sie Ihren Posteingang.
              </Alert>
            ) : (
              <form onSubmit={formik.handleSubmit}>
                <TextField
                  fullWidth
                  id="email"
                  name="email"
                  label="E-Mail-Adresse"
                  type="email"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  error={formik.touched.email && Boolean(formik.errors.email)}
                  helperText={formik.touched.email && formik.errors.email}
                  margin="normal"
                  autoComplete="email"
                  autoFocus
                />
                <Button
                  color="primary"
                  variant="contained"
                  fullWidth
                  type="submit"
                  disabled={loading}
                  sx={{ mt: 3, mb: 2, height: 48 }}
                >
                  {loading ? <CircularProgress size={24} /> : 'Reset-Link senden'}
                </Button>
              </form>
            )}

            <Box textAlign="center" sx={{ mt: 2 }}>
              <Link component={RouterLink} to="/login" variant="body2">
                Zurück zum Login
              </Link>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </>
  );
};

export default ForgotPasswordPage;
